import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useClubPlan } from './club-dev-plan/hooks/useClubPlan';
import VisionValuesSection from './club-dev-plan/VisionValuesSection';
import SWOTSection from './club-dev-plan/SWOTSection';
import ThemesSection from './club-dev-plan/ThemesSection';
import { getPlan, savePlan } from '../api/clubDevPlanClient';

const SECTION_INPUT =
  'w-full px-3 py-2 border border-outline rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary text-on-surface bg-surface';

export default function ClubDevelopmentPlanTab({ token }) {
  const { state, actions } = useClubPlan();

  const [expanded, setExpanded] = useState({
    clubInfo: true,
    vision: false,
    situation: false,
    swot: false,
    themes: false,
  });
  const [loadState, setLoadState] = useState('loading'); // loading | ready | error
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
  const [saveError, setSaveError] = useState('');
  const [dataVersion, setDataVersion] = useState(0); // bump remounts sub-components after load

  // Only genuine user edits should trigger a server save — never the initial
  // load/import. This prevents echoing loaded data back and prevents a failed
  // load from clobbering the server with a stale local draft.
  const userEdited = useRef(false);
  const markEdited = useCallback(() => {
    userEdited.current = true;
  }, []);

  const toggle = (key) => setExpanded((p) => ({ ...p, [key]: !p[key] }));

  // Load the plan from the server on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getPlan(token);
        if (cancelled) return;
        if (res.exists && res.plan) {
          actions.importFromJSON(JSON.stringify(res.plan));
          setDataVersion((v) => v + 1);
        }
        setLoadState('ready');
      } catch (err) {
        if (cancelled) return;
        console.error('Load plan failed:', err);
        // Keep editing against the local draft; just surface that we're offline.
        setLoadState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const doSave = useCallback(async () => {
    setSaveStatus('saving');
    setSaveError('');
    try {
      await savePlan(token, state);
      setSaveStatus('saved');
    } catch (err) {
      setSaveStatus('error');
      setSaveError(err.message || 'Save failed');
    }
  }, [token, state]);

  // Debounced auto-save on user edits.
  useEffect(() => {
    if (!userEdited.current) return;
    const t = setTimeout(() => {
      doSave();
    }, 1200);
    return () => clearTimeout(t);
  }, [state, doSave]);

  const handleExport = () => {
    const json = actions.getExportData();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `egac-club-dev-plan-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const setClubInfo = (field, value) => {
    markEdited();
    actions.updateClubInfo({ [field]: value });
  };
  const setSituation = (field, value) => {
    markEdited();
    actions.updateSituation({ [field]: value });
  };

  const saveStatusLabel = {
    idle: '',
    saving: 'Saving…',
    saved: '✓ Saved',
    error: '⚠ Not saved',
  }[saveStatus];

  const saveStatusClass = {
    idle: 'text-on-surface-variant',
    saving: 'text-on-surface-variant',
    saved: 'text-primary',
    error: 'text-error',
  }[saveStatus];

  if (loadState === 'loading') {
    return (
      <div className="card">
        <p className="text-on-surface-variant">Loading club development plan…</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-8">
      {/* Header */}
      <div className="card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold font-headline mb-2 text-on-surface">
              Club Development Plan
            </h2>
            <p className="text-on-surface-variant">
              Strategic plan for club development and growth
            </p>
          </div>
          <div className="text-right whitespace-nowrap">
            <span className={`text-sm font-medium ${saveStatusClass}`}>{saveStatusLabel}</span>
          </div>
        </div>
        {loadState === 'error' && (
          <p className="mt-2 text-sm text-error">
            Couldn’t reach the server — you can keep editing, changes are kept in this browser and
            will save once the connection is back.
          </p>
        )}
        {saveStatus === 'error' && saveError && (
          <p className="mt-2 text-sm text-error">Save failed: {saveError}</p>
        )}
      </div>

      {/* Club Info Section */}
      <div className="card">
        <button
          onClick={() => toggle('clubInfo')}
          className="w-full flex items-center justify-between p-4 hover:bg-surface-container-high rounded-lg transition-colors"
        >
          <h3 className="text-lg font-bold font-headline text-on-surface">📋 Club Info</h3>
          <span className="text-lg">{expanded.clubInfo ? '▼' : '▶'}</span>
        </button>

        {expanded.clubInfo && (
          <div className="px-4 pb-4 space-y-4 border-t border-outline-variant">
            <div>
              <label className="block text-sm font-semibold text-on-surface mb-2">Club Name</label>
              <input
                type="text"
                value={state.clubInfo.clubName}
                onChange={(e) => setClubInfo('clubName', e.target.value)}
                className={SECTION_INPUT}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-on-surface mb-2">
                  Published Date
                </label>
                <input
                  type="date"
                  value={state.clubInfo.publishedDate}
                  onChange={(e) => setClubInfo('publishedDate', e.target.value)}
                  className={SECTION_INPUT}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-on-surface mb-2">
                  Action Review Date
                </label>
                <input
                  type="date"
                  value={state.clubInfo.actionReviewDate}
                  onChange={(e) => setClubInfo('actionReviewDate', e.target.value)}
                  className={SECTION_INPUT}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-on-surface mb-2">
                  Plan Review Date
                </label>
                <input
                  type="date"
                  value={state.clubInfo.planReviewDate}
                  onChange={(e) => setClubInfo('planReviewDate', e.target.value)}
                  className={SECTION_INPUT}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-on-surface mb-2">
                  Written By
                </label>
                <input
                  type="text"
                  value={state.clubInfo.writtenBy}
                  onChange={(e) => setClubInfo('writtenBy', e.target.value)}
                  className={SECTION_INPUT}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Vision & Values Section */}
      <div className="card">
        <button
          onClick={() => toggle('vision')}
          className="w-full flex items-center justify-between p-4 hover:bg-surface-container-high rounded-lg transition-colors"
        >
          <h3 className="text-lg font-bold font-headline text-on-surface">🎯 Vision &amp; Values</h3>
          <span className="text-lg">{expanded.vision ? '▼' : '▶'}</span>
        </button>

        {expanded.vision && (
          <div className="px-4 pb-4 border-t border-outline-variant pt-4">
            <VisionValuesSection
              key={`vv-${dataVersion}`}
              initialData={state.strategic}
              onSave={(d) => {
                markEdited();
                actions.updateVision(d.vision);
                actions.updateValues(d.values);
                actions.updateGoals(d.goals);
              }}
            />
          </div>
        )}
      </div>

      {/* Current Situation Section */}
      <div className="card">
        <button
          onClick={() => toggle('situation')}
          className="w-full flex items-center justify-between p-4 hover:bg-surface-container-high rounded-lg transition-colors"
        >
          <h3 className="text-lg font-bold font-headline text-on-surface">📊 Current Situation</h3>
          <span className="text-lg">{expanded.situation ? '▼' : '▶'}</span>
        </button>

        {expanded.situation && (
          <div className="px-4 pb-4 space-y-4 border-t border-outline-variant">
            <div>
              <label className="block text-sm font-semibold text-on-surface mb-2 mt-4">
                Current Status
              </label>
              <textarea
                value={state.situation.currentSituation}
                onChange={(e) => setSituation('currentSituation', e.target.value)}
                placeholder="Describe the current state of the club..."
                className={`${SECTION_INPUT} resize-none`}
                rows={4}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-on-surface mb-2">
                Recent History
              </label>
              <textarea
                value={state.situation.recentHistory}
                onChange={(e) => setSituation('recentHistory', e.target.value)}
                placeholder="Summarise key events and changes from the past year..."
                className={`${SECTION_INPUT} resize-none`}
                rows={4}
              />
            </div>
          </div>
        )}
      </div>

      {/* SWOT Analysis Section */}
      <div className="card">
        <button
          onClick={() => toggle('swot')}
          className="w-full flex items-center justify-between p-4 hover:bg-surface-container-high rounded-lg transition-colors"
        >
          <h3 className="text-lg font-bold font-headline text-on-surface">⚡ SWOT Analysis</h3>
          <span className="text-lg">{expanded.swot ? '▼' : '▶'}</span>
        </button>

        {expanded.swot && (
          <div className="px-4 pb-4 border-t border-outline-variant pt-4">
            <SWOTSection
              key={`swot-${dataVersion}`}
              initialData={state.swot}
              onSave={(d) => {
                markEdited();
                actions.updateSWOT(d);
              }}
            />
          </div>
        )}
      </div>

      {/* Development Themes Section */}
      <div className="card">
        <button
          onClick={() => toggle('themes')}
          className="w-full flex items-center justify-between p-4 hover:bg-surface-container-high rounded-lg transition-colors"
        >
          <h3 className="text-lg font-bold font-headline text-on-surface">🎯 Development Themes</h3>
          <span className="text-lg">{expanded.themes ? '▼' : '▶'}</span>
        </button>

        {expanded.themes && (
          <div className="px-4 pb-4 border-t border-outline-variant pt-4">
            <ThemesSection
              key={`themes-${dataVersion}`}
              initialThemes={state.themes}
              onSave={(themesData) => {
                markEdited();
                Object.entries(themesData).forEach(([name, objectives]) => {
                  actions.updateThemes(name, objectives);
                });
              }}
            />
          </div>
        )}
      </div>

      {/* Export Section */}
      <div className="card flex flex-col sm:flex-row gap-3 justify-between items-center">
        <div>
          <h3 className="font-bold text-on-surface">Plan Data</h3>
          <p className="text-xs text-on-surface-variant">Download a JSON backup of the whole plan</p>
        </div>
        <button onClick={handleExport} className="btn-primary text-sm px-4 py-2">
          📥 Export Plan
        </button>
      </div>
    </div>
  );
}
